import { useState, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import countries from "i18n-iso-countries/index.js";
import { apiSend } from "@/lib/api-error";
import { flagEmoji } from "@/lib/flag-emoji";
import { CaretDown, Check, Crosshair, MagnifyingGlass } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface CountryInfo {
  code: string;
  name: string;
}

interface PricingRegionChipProps {
  resolvedCountry: string | null;
  className?: string;
}

export function PricingRegionChip({ resolvedCountry, className }: PricingRegionChipProps) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  const current = resolvedCountry;
  const currentName = current
    ? countries.getName(current, i18n.language, { select: "official" }) ?? current
    : t("country.other");

  const locale = i18n.language === "bs" ? "bs" : "en";
  const allCountries: CountryInfo[] = useMemo(() => {
    const names = countries.getNames(locale, { select: "official" });
    return Object.entries(names)
      .map(([code, name]) => ({ code, name }))
      .sort((a, b) => a.name.localeCompare(b.name, locale));
  }, [locale]);

  const filtered = useMemo(() => {
    if (!search.trim()) return allCountries;
    const s = search.trim().toLowerCase();
    return allCountries.filter(
      (c) => c.name.toLowerCase().includes(s) || c.code.toLowerCase().includes(s),
    );
  }, [allCountries, search]);

  const overrideMutation = useMutation({
    mutationFn: (countryCode: string | null) =>
      apiSend(
        "/api/country-override",
        "POST",
        { countryCode },
        "Failed to change country",
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["country"] });
      queryClient.invalidateQueries({ queryKey: ["services"] });
      queryClient.invalidateQueries({ queryKey: ["therapist", "services"] });
      setOpen(false);
      setSearch("");
    },
  });

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={ref} className={cn("relative inline-block", className)}>
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        aria-expanded={open}
        className={cn(
          "group inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-muted/30 pl-2 pr-2.5 py-1 text-[11px] font-medium text-muted-foreground transition-all",
          "hover:border-primary/30 hover:bg-muted/60 hover:text-foreground",
          open && "border-primary/30 bg-muted/60 text-foreground",
        )}
      >
        <span className="text-sm leading-none">{flagEmoji(current)}</span>
        <span className="truncate">
          {t("country.pricesShownFor", {
            flag: "",
            country: currentName,
          }).replace(/^\s+/, "")}
        </span>
        <CaretDown
          className={cn(
            "size-3 shrink-0 opacity-50 transition-transform",
            open && "rotate-180",
          )}
        />
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1.5 flex max-h-80 w-64 flex-col overflow-hidden rounded-xl border border-border/60 bg-background shadow-lg">
          <div className="border-b border-border/40 p-2">
            <div className="relative">
              <MagnifyingGlass className="absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground z-10" />
              <Input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("country.searchPlaceholder")}
                className="h-8 pl-7"
                autoFocus
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            <button
              type="button"
              onClick={() => overrideMutation.mutate(null)}
              disabled={overrideMutation.isPending}
              className="flex w-full items-center gap-2 border-b border-border/30 px-3 py-2 text-xs text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground"
            >
              <Crosshair className="size-3.5" />
              <span>{t("country.detectFromLocation")}</span>
            </button>
            {filtered.map((c) => {
              const active = c.code === current;
              return (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => overrideMutation.mutate(c.code)}
                  disabled={overrideMutation.isPending}
                  className={cn(
                    "flex w-full items-center gap-2.5 px-3 py-2 text-sm transition-colors",
                    active
                      ? "bg-muted/60 text-foreground font-medium"
                      : "text-muted-foreground hover:bg-muted/40 hover:text-foreground",
                  )}
                >
                  <span>{flagEmoji(c.code)}</span>
                  <span className="flex-1 truncate text-left">{c.name}</span>
                  {active && <Check className="size-3.5 shrink-0 text-primary" />}
                </button>
              );
            })}
            {filtered.length === 0 && (
              <div className="px-3 py-4 text-center text-xs text-muted-foreground">
                —
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
